/* eslint-disable camelcase */
import React, { useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { getAllApprovedPortfolio, getCandidatTransactions } from '../../data/store/account/accountActions';

import { Button, Col, Row } from 'antd';
// import Skeleton from 'react-loading-skeleton';
import CustomSearchInput from '../../components/CustomSearchInput/CustomSearchInput';

import { ReactComponent as Filter } from '../../assets/filter-icon.svg';
import { ReactComponent as Delete } from '../../assets/delete-black-icon.svg';
import { ReactComponent as Quantify } from '../../assets/quantify-icon.svg';
import dayjs from 'dayjs';
import ModalViewPP from './ModalViewPP';
import ModalDeleteCandidat from './ModalDeleteCandidat';
import ModalFilterCandidat from './ModalFilterCandidat';
import ModalTransaction from './ModalTransaction';
import { getCurrency } from '../../data/store/instruments/instrumentsActions';
import CustomSortIcon from '../../components/CustomSortIcon/CustomSortIcon';
import CustomTable from '../../components/CustomTable/CustomTable';

const AdminApprovedTable = () => {
  const { t } = useTranslation('');
  const dispatch = useDispatch();

  const { approved, isLoading } = useSelector((state) => state.accountReducer);
  const advisors = useSelector((state) => state.advisorReducer.users.list);
  const accounts = useSelector((state) => state.accountReducer?.accounts?.list);

  const [tableColumns, setTableColumns] = useState();
  const [filteredPortfolio, setFilteredPortfolio] = useState([]);
  const [filter, setFilter] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [filterCounter, setFilterCounter] = useState(null);
  const [viewReport, setViewReport] = useState('');
  const [modalDeleteCandidat, setModalDeleteCandidat] = useState(0);

  const [quantify, setQuantify] = useState(null);

  useEffect(() => {
    dispatch(getAllApprovedPortfolio());
    dispatch(getCurrency());
    return () => {
      dispatch({ type: 'CLEAR_APPROVED_PORTFOLIO' });
    };
  }, []);

  const applyFilters = () => {
    if (!filter) return setFilteredPortfolio(approved);
    const { ...rest } = filter;
    // By created
    const filteredByDate = approved.filter(item => {
      const createDate = dayjs(item.create_date);
      if (!rest.create_start_date && !rest.create_end_date) {
        return true;
      }
      if (!rest.create_start_date) {
        return createDate.isBefore(rest.create_end_date, 'day') || createDate.isSame(rest.create_end_date, 'day');
      }
      if (!rest.create_end_date) {
        return createDate.isAfter(rest.create_start_date, 'day') || createDate.isSame(rest.create_start_date, 'day');
      }
      return (createDate.isAfter(rest.create_start_date, 'day') || createDate.isSame(rest.create_start_date, 'day')) &&
              (createDate.isBefore(rest.create_end_date, 'day') || createDate.isSame(rest.create_end_date, 'day'));
    });
    // By approved
    const filteredByApproved = filteredByDate.filter(item => {
      const approvedDate = dayjs(item.approve_date);
      if (!rest.approved_start_date && !rest.approved_end_date) {
        return true;
      }
      if (!rest.approved_start_date) {
        return approvedDate.isBefore(rest.approved_end_date, 'day') || approvedDate.isSame(rest.approved_end_date, 'day');
      }
      if (!rest.create_end_date) {
        return approvedDate.isAfter(rest.approved_start_date, 'day') || approvedDate.isSame(rest.approved_start_date, 'day');
      }
      return (approvedDate.isAfter(rest.approved_start_date, 'day') || approvedDate.isSame(rest.approved_start_date, 'day')) &&
              (approvedDate.isBefore(rest.approved_end_date, 'day') || approvedDate.isSame(rest.approved_end_date, 'day'));
    });

    let filteredByPortfolio = [];
    if (rest.portfolioList[0] === 'All') {
      filteredByPortfolio = filteredByApproved;
    } else {
      filteredByPortfolio = filteredByApproved.filter(i => rest.portfolioList.includes(i.name));
    }

    let filteredByAdvisor = [];
    if (rest.advisorsList[0] === 'All') {
      filteredByAdvisor = filteredByPortfolio;
    } else {
      filteredByAdvisor = filteredByPortfolio.filter(i => rest.advisorsList.includes(i.advisor_id));
    }

    let filteredByClient = [];
    if (rest.clientsList[0] === 'All') {
      filteredByClient = filteredByAdvisor;
    } else {
      filteredByClient = filteredByAdvisor.filter(i => rest.clientsList.includes(i.client_id));
    }

    setFilteredPortfolio(filteredByClient);
  };

  useEffect(() => {
    applyFilters();
  }, [filter]);

  useEffect(() => {
    if (approved) {
      setFilteredPortfolio(approved);
    } else {
      setFilteredPortfolio([]);
    }
  }, [approved]);

  const textSort = (a, b, direction = 1) => {
    if (a?.toLowerCase() < b?.toLowerCase()) {
      return -1 * direction;
    }
    if (a?.toLowerCase() > b?.toLowerCase()) {
      return 1 * direction;
    }
    return 0;
  };

  const getAdvisorName = (id) => {
    const advisor = advisors?.find(i => i.ID === id);
    const firstName = advisor?.profile?.firstName ?? '';
    const lastName = advisor?.profile?.lastName ?? '';
    return firstName || lastName ? `${firstName} ${lastName}` : 'Super Admin';
  };

  const getClientName = (id) => {
    const account = accounts?.find(i => i.id === id);
    const name = account?.name ?? '';
    return name;
  };

  useEffect(() => {
    const defaultColumns = [
      {
        title: t('ADVISOR'),
        dataIndex: 'advisor_id',
        className: 'suggestions-name',
        width: '',
        align: 'left',
        render: (value) => getAdvisorName(value),
        sorter: (a, b) => textSort(getAdvisorName(a), getAdvisorName(b)),
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
      {
        title: t('CLIENT'),
        dataIndex: 'client_id',
        className: 'suggestions-name',
        width: '',
        align: 'left',
        render: (value) => getClientName(value),
        sorter: (a, b) => textSort(getClientName(a), getClientName(b)),
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
      {
        title: t('PORTFOLIO_NUMBER'),
        dataIndex: 'portfolio_code',
        className: 'suggestions-name',
        width: '',
        align: 'left',
        sorter: (a, b) => textSort(a.name, b.name),
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
      {
        title: t('CREATION_DATE'),
        dataIndex: 'create_date',
        className: 'suggestions-create-date',
        width: '',
        align: 'left',
        render: (value) => <><span className='create-date'>{dayjs(value).format('MMM DD  YYYY')}</span><br/>
          <span className='suggestions-create-time'>{dayjs(value).format('HH:mm A')}</span></>,
        sorter: (a, b) => textSort(a.create_date, b.create_date),
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
      {
        title: t('APPROVAL_DATE'),
        dataIndex: 'approve_date',
        className: 'suggestions-approval-date',
        width: '',
        align: 'left',
        render: (value) => <><span className='create-date'>{dayjs(value).format('MMM DD  YYYY')}</span><br/>
          <span className='suggestions-create-time'>{dayjs(value).format('HH:mm A')}</span></>,
        sorter: (a, b) => textSort(a.approve_date, b.approve_date),
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
      {
        title: t('ACTIONS'),
        align: 'center',
        dataIndex: 'operation',
        width: '15%',
        render: (_, record) => {
          return (
            <div className="cell-button-wrapper">
              <Button
                icon={<Quantify />}
                iconPosition='end'
                className="action-btn advisor-action-btn approve"
                onClick={() => handlQuantify(record.id, record.portfolio_id)}
              >
                {t('CALCULATE')}
              </Button>
              <Button type="text" onClick={() => handleDelete(record.id)}>
                <Delete />
              </Button>
            </div>
          );
        },
      },
    ];
    setTableColumns(defaultColumns);
  }, [approved, advisors, accounts]);

  const handeleFilter = () => {
    setOpenFilter(true);
  };

  const handleCloseFilter = () => {
    setOpenFilter(false);
  };

  const handleSearch = (event) => {
    const { value } = event.target;
    if (value?.length > 2) {
      const searched = approved?.filter(
        (i) =>
          i.name?.toLowerCase().includes(value?.toLowerCase())
      );
      setFilteredPortfolio(searched);
    } else {
      setFilteredPortfolio(approved);
    }
  };

  const handlQuantify = (candidatId, portfolio_id) => {
    setQuantify(candidatId);
    dispatch(getCandidatTransactions({ portfolio_id, candidatId }));
  };

  const handleDelete = (id) => {
    setModalDeleteCandidat(id);
  };

  const getCandidatById = useMemo(() => {
    return approved?.find(i => i.id === modalDeleteCandidat);
  }, [approved, modalDeleteCandidat]);

  const closeModalTransaction = () => {
    setQuantify(null);
  };

  return (
    <div>
      <Row justify={'end'} className='search-wrapper'>
        <Col span={12} className="instrument-button-wraper">
          <CustomSearchInput
            className='account-select-search suggested'
            placeholder={t('SEARCH')}
            onChange={handleSearch}
          />
          <Button
            className="filter-btn"
            type="default"
            disabled={isLoading}
            onClick={handeleFilter}
            icon={<Filter />}
            iconPosition="end"
          >
            {t('FILTER')}
            {filterCounter
              ? (
              <span className="filter-counter">{filterCounter}</span>
                )
              : (
                  ''
                )}
          </Button>
        </Col>
      </Row>
      <div className="client portfolio-table suggestions-table">
          <CustomTable
            rowKey={(record) => record.id}
            loading={isLoading}
            columns={tableColumns}
            dataSource={filteredPortfolio}
            size="middle"
            pagination={false}
          />
      </div>
      <ModalViewPP
        viewReport={viewReport}
        setViewReport={setViewReport}
      />
      <ModalDeleteCandidat
        text='DELETE_THIS_APPROVED'
        candidatType={'admin-approved'}
        userId={getCandidatById?.client_id}
        name={getCandidatById?.name ?? ''}
        modalDeleteCandidat={modalDeleteCandidat}
        setModalDeleteCandidat={setModalDeleteCandidat}
      />
      <ModalFilterCandidat
        type='admin-approved'
        source={approved}
        setFilter={setFilter}
        openFilter={openFilter}
        handleCloseFilter={handleCloseFilter}
        getAdvisorName={getAdvisorName}
        getClientName={getClientName}
      />
      <ModalTransaction
        openModalTransaction={quantify}
        closeModalTransaction={closeModalTransaction}
        approved={approved}
      />
    </div>
  );
};

export default AdminApprovedTable;

AdminApprovedTable.propTypes = {
  data: PropTypes.array,
};
